import { lazy, Suspense } from 'react';
import { createBrowserRouter, createHashRouter } from 'react-router-dom';
import { AppLayout } from './components/app-layout.js';
import { clinicRoutes } from './clinic-routes.js';
import { toolsRoutes } from './tools-routes.js';
import { practiceRoutes } from './practice-routes.js';
import { HomeRoute } from './routes/home-route.js';
import { BookingTab } from './routes/booking-tab.js';
import { LessonsTab } from './routes/lessons-tab.js';
import { CoachTodayRedirect } from './routes/coach-today-redirect.js';
import { AccountRoute } from './routes/account-route.js';
import { PersonRoute } from './routes/person-route.js';
import { AddChildRoute } from './routes/add-child-route.js';
import { SlotTab } from './routes/slot-tab.js';
import { AssessmentTimesRoute } from './routes/assessment-times-route.js';
import { CartRoute } from './routes/cart-route.js';
import { BookedRoute } from './routes/booked-route.js';
import { LessonRoute } from './routes/lesson-route.js';
import { ParentCancelRoute } from './routes/parent-cancel-route.js';
import { AthleteLessonRoute } from './routes/athlete-lesson-route.js';
import { ProfileRoute } from './routes/profile-route.js';
import { MyLessonsRoute } from './routes/my-lessons-route.js';
import { MyLessonRoute } from './routes/my-lesson-route.js';
import { AthleteSignInSetupRoute } from './routes/athlete-sign-in-setup-route.js';
import { WelcomeRoute } from './routes/welcome-route.js';
import { JoinRoute } from './routes/join-route.js';
import { InviteParentRoute } from './routes/invite-parent-route.js';
import { OtherParentRoute } from './routes/other-parent-route.js';
import { NotFoundRoute } from './routes/not-found-route.js';
import { TokenRoute } from './routes/token-route.js';
import { WaitNote } from './components/wait-note.js';

/*
 * The coach's availability screen is the one route most people here will never
 * open, and it is the largest: a week grid, a pane and five commands. Loading
 * it lazily means a parent's browser never fetches it at all, which is most of
 * what a separate coach app would have bought.
 *
 * It is NOT guarded here. /api/me says whether somebody is a coach and the
 * Account panel offers the link accordingly, but every availability route
 * resolves the coach from the token and refuses anyone else — a route table is
 * not a permission system, and a boolean in a browser is a hint.
 */
const HoursRoute = lazy(() =>
  import('./routes/hours-route.js').then((module) => ({ default: module.HoursRoute })),
);

/*
 * The coach's book of work, and one lesson in it. Lazy for the same reason as
 * the grid: a parent never opens either, and a note editor is weight they
 * should not carry. Guarded by the API, not by the route table — every coach
 * route resolves the coach from the token, and the day list returns only
 * lessons in hours that coach published.
 */

const CoachLessonRoute = lazy(() =>
  import('./routes/coach-lesson-route.js').then((module) => ({
    default: module.CoachLessonRoute,
  })),
);

const CoachCancelRoute = lazy(() =>
  import('./routes/coach-cancel-route.js').then((module) => ({
    default: module.CoachCancelRoute,
  })),
);

/* Clients (mockup *Clients and Texted Links* v8, approved 21 October): the coach's, lazy as the rest. */
const CoachClientsRoute = lazy(() =>
  import('./routes/coach-clients-route.js').then((module) => ({ default: module.CoachClientsRoute })),
);
const CoachAddFamilyRoute = lazy(() =>
  import('./routes/coach-add-family-route.js').then((module) => ({ default: module.CoachAddFamilyRoute })),
);
const CoachFamilyRoute = lazy(() =>
  import('./routes/coach-family-route.js').then((module) => ({ default: module.CoachFamilyRoute })),
);
const CoachCreditsRoute = lazy(() => import('./routes/coach-credits-route.js').then((m) => ({ default: m.CoachCreditsRoute })));
const CoachSignatureViewRoute = lazy(() =>
  import('./routes/coach-signature-view-route.js').then((module) => ({ default: module.CoachSignatureViewRoute })),
);
const FamilySignatureRoute = lazy(() =>
  import('./routes/family-signature-route.js').then((module) => ({ default: module.FamilySignatureRoute })),
);
const AthleteSignatureRoute = lazy(() =>
  import('./routes/athlete-signature-route.js').then((module) => ({ default: module.AthleteSignatureRoute })),
);
const SettingsRoute = lazy(() =>
  import('./routes/settings-route.js').then((module) => ({ default: module.SettingsRoute })),
);
const TeamsRoute = lazy(() => import('./routes/teams-route.js').then((module) => ({ default: module.TeamsRoute })));
const TeamNewRoute = lazy(() => import('./routes/team-new-route.js').then((module) => ({ default: module.TeamNewRoute })));
const TeamRoute = lazy(() => import('./routes/team-route.js').then((module) => ({ default: module.TeamRoute })));
const TeamPlayerRoute = lazy(() => import('./routes/team-player-route.js').then((module) => ({ default: module.TeamPlayerRoute })));
/* My Team (Teams stage 3, approved 25-26 October): athletes, team coaches and parents. */
const MyTeamRoute = lazy(() => import('./routes/my-team-route.js').then((module) => ({ default: module.MyTeamRoute })));
const TeamAthleteRoute = lazy(() => import('./routes/team-athlete-route.js').then((module) => ({ default: module.TeamAthleteRoute })));
const TeamAthleteSignatureRoute = lazy(() => import('./routes/team-athlete-signature-route.js').then((module) => ({ default: module.TeamAthleteSignatureRoute })));
const TeamLessonRoute = lazy(() => import('./routes/team-lesson-route.js').then((module) => ({ default: module.TeamLessonRoute })));
const CoachOwnSignatureRoute = lazy(() =>
  import('./routes/coach-own-signature-route.js').then((module) => ({ default: module.CoachOwnSignatureRoute })),
);
const OwnSignatureRoute = lazy(() =>
  import('./routes/own-signature-route.js').then((module) => ({ default: module.OwnSignatureRoute })),
);
const CoachAthleteRoute = lazy(() =>
  import('./routes/coach-athlete-route.js').then((module) => ({ default: module.CoachAthleteRoute })),
);
const CoachImportRoute = lazy(() => import('./routes/coach-import-route.js').then((module) => ({ default: module.CoachImportRoute })));
const CoachEditFamilyRoute = lazy(() =>
  import('./routes/coach-edit-family-route.js').then((module) => ({ default: module.CoachEditFamilyRoute })),
);
/* Recul from the lesson page (stage 4, approved 25 October): the coach's, lazy as the rest. */
const CoachReculRoute = lazy(() =>
  import('./routes/coach-recul-route.js').then((module) => ({ default: module.CoachReculRoute })),
);


/**
 * Exported as a plain definition rather than wired straight into a provider, so
 * the route table can be asserted in a test without rendering a browser.
 */
export const ROUTE_PATHS = [
  '/',
  '/booking',
  '/booking/assessments',
  '/booking/clinics/:clinicId',
  '/booking/:slotId',
  '/clinics/:token',
  '/coach/clinics/new',
  '/coach/clinics/:clinicId',
  '/coach/clinics/:clinicId/edit',
  '/cart',
  '/cart/done',
  '/hours',
  '/coach/lessons',
  '/coach/lessons/:lessonId',
  '/coach/lessons/:lessonId/cancel',
  '/coach/clients',
  '/coach/clients/new',
  '/settings/teams/import',
  '/coach/clients/:adultAccountId',
  '/coach/clients/:adultAccountId/edit',
  '/coach/clients/:adultAccountId/credits/add',
  '/lessons',
  '/lessons/:bookingId',
  '/lessons/:bookingId/:lessonId',
  '/lessons/:bookingId/cancel/:lessonId',
  '/account',
  '/account/people/new',
  '/account/people/:clientId',
  '/account/other-adult',
  '/account/other-adult/new',
  '/profile',
  '/my',
  '/my/:lessonId',
  '/athlete-sign-in',
  '/tools',
  '/tools/who',
  '/tools/practice/:practiceId',
  '/welcome',
  '/join',
] as const;

/*
 * /token decodes a real access token so the claims Entra actually issues can be
 * read rather than assumed. Development only - it is not registered in a
 * production build, so it cannot ship by being forgotten. Remove it once the
 * API middleware is built and its claims are settled.
 */
const devOnlyRoutes = import.meta.env.DEV ? [{ path: 'token', element: <TokenRoute /> }] : [];

/*
 * Azure Static Web Apps rewrites unknown paths to index.html, so the real
 * deployment uses a browser router and gets clean URLs. A plain static host
 * (a preview build, a file drop) has no rewrite, and a hard refresh on /lessons
 * would 404 there. VITE_ROUTER=hash switches to hash routing for those, without
 * the app carrying a permanent `#` it does not need in production.
 */
const create = import.meta.env['VITE_ROUTER'] === 'hash' ? createHashRouter : createBrowserRouter;

/* What a lazy screen shows while it arrives (RE-35: every wait shows it is working). */
const opening = <WaitNote className="empty">Opening…</WaitNote>;

export const router = create([
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <HomeRoute /> },
      { path: 'booking', element: <BookingTab /> },
      { path: 'booking/assessments', element: <AssessmentTimesRoute /> },
      ...clinicRoutes,
      { path: 'booking/:slotId', element: <SlotTab /> },
      /*
       * `done` before the cart's own path would be ambiguous the other way
       * round only if the cart took a parameter, which it does not: there is one
       * live cart per account and every route finds it from the token.
       */
      { path: 'cart', element: <CartRoute /> },
      { path: 'cart/done', element: <BookedRoute /> },
      {
        path: 'hours',
        element: (
          <Suspense fallback={opening}>
            <HoursRoute />
          </Suspense>
        ),
      },
      {
        // The one-day view gave way to the Lessons tab (16 October); its old
        // address opens that tab on today.
        path: 'coach/lessons',
        element: <CoachTodayRedirect />,
      },
      {
        path: 'coach/lessons/:lessonId',
        element: (
          <Suspense fallback={opening}>
            <CoachLessonRoute />
          </Suspense>
        ),
      },
      {
        // The whole screen: AppLayout leaves out the header and tabs for any
        // address ending /recul.
        path: 'coach/lessons/:lessonId/recul',
        element: (
          <Suspense fallback={opening}>
            <CoachReculRoute />
          </Suspense>
        ),
      },
      {
        path: 'coach/lessons/:lessonId/cancel',
        element: (
          <Suspense fallback={opening}>
            <CoachCancelRoute />
          </Suspense>
        ),
      },
      { path: 'coach/clients', element: (
          <Suspense fallback={opening}>
            <CoachClientsRoute />
          </Suspense>
        ) },
      { path: 'coach/clients/new', element: (
          <Suspense fallback={opening}>
            <CoachAddFamilyRoute />
          </Suspense>
        ) },
      { path: 'coach/clients/:adultAccountId', element: (
          <Suspense fallback={opening}>
            <CoachFamilyRoute />
          </Suspense>
        ) },
      { path: 'coach/clients/:adultAccountId/athletes/:clientId', element: (
          <Suspense fallback={opening}>
            <CoachAthleteRoute />
          </Suspense>
        ) },
      /* "View as [athlete] sees it" (Performance Signature stage 4a): the hub, then each section. */
      { path: 'coach/clients/:adultAccountId/athletes/:clientId/view', element: (
          <Suspense fallback={opening}>
            <CoachSignatureViewRoute />
          </Suspense>
        ) },
      { path: 'coach/clients/:adultAccountId/athletes/:clientId/view/:section', element: (
          <Suspense fallback={opening}>
            <CoachSignatureViewRoute />
          </Suspense>
        ) },
      { path: 'coach/clients/:adultAccountId/credits/add', element: <Suspense fallback={opening}><CoachCreditsRoute /></Suspense> },
      { path: 'coach/clients/:adultAccountId/edit', element: (
          <Suspense fallback={opening}>
            <CoachEditFamilyRoute />
          </Suspense>
        ) },
      /* Settings, behind the MPC coach's gear: Teams (028; approved 25 October).
         `new` before the id, or it would be read as a team id. */
      { path: 'settings', element: <Suspense fallback={opening}><SettingsRoute /></Suspense> },
      { path: 'settings/teams', element: <Suspense fallback={opening}><TeamsRoute /></Suspense> },
      { path: 'settings/teams/new', element: <Suspense fallback={opening}><TeamNewRoute /></Suspense> },
      /* Add from a spreadsheet lives with Teams, where the people it adds are put to use (RE-35, 27 October). */
      { path: 'settings/teams/import', element: <Suspense fallback={opening}><CoachImportRoute /></Suspense> },
      { path: 'settings/teams/:teamId', element: <Suspense fallback={opening}><TeamRoute /></Suspense> },
      { path: 'settings/teams/:teamId/players/:clientId', element: <Suspense fallback={opening}><TeamPlayerRoute /></Suspense> },
      /* My Team (stage 3): opened from a My Team pill; ‹ back as ?child= says. */
      { path: 'teams/:teamId', element: (
          <Suspense fallback={opening}>
            <MyTeamRoute />
          </Suspense>
        ) },
      { path: 'teams/:teamId/players/:clientId', element: (
          <Suspense fallback={opening}>
            <TeamAthleteRoute />
          </Suspense>
        ) },
      /* A shared lesson, for the team's coaches (030, board c). */
      { path: 'teams/:teamId/players/:clientId/lessons/:lessonId', element: (
          <Suspense fallback={opening}>
            <TeamLessonRoute />
          </Suspense>
        ) },
      { path: 'teams/:teamId/players/:clientId/signature/:section', element: (
          <Suspense fallback={opening}>
            <TeamAthleteSignatureRoute />
          </Suspense>
        ) },
      { path: 'lessons', element: <LessonsTab /> },
      { path: 'lessons/:bookingId', element: <LessonRoute /> },
      { path: 'lessons/:bookingId/:lessonId', element: <AthleteLessonRoute /> },
      { path: 'lessons/:bookingId/cancel/:lessonId', element: <ParentCancelRoute /> },
      { path: 'account', element: <AccountRoute /> },
      /*
       * `new` is declared BEFORE the parameter, or react-router matches it as a
       * client id and the Add a child form becomes a person page for somebody
       * called "new".
       */
      /* A parent's or the coach's own Performance Signature (25 October). */
      { path: 'account/signature', element: (
          <Suspense fallback={opening}>
            <OwnSignatureRoute />
          </Suspense>
        ) },
      { path: 'account/signature/:section', element: (
          <Suspense fallback={opening}>
            <OwnSignatureRoute />
          </Suspense>
        ) },
      { path: 'coach/signature', element: (
          <Suspense fallback={opening}>
            <CoachOwnSignatureRoute />
          </Suspense>
        ) },
      { path: 'account/people/new', element: <AddChildRoute /> },
      { path: 'account/people/:clientId', element: <PersonRoute /> },
      /* A child's Performance Signature section, for a parent (stage 4b). */
      { path: 'account/people/:clientId/signature/:section', element: (
          <Suspense fallback={opening}>
            <FamilySignatureRoute />
          </Suspense>
        ) },
      /* The other parent (canvas row D): invited from Account, and their page. */
      { path: 'account/other-adult/new', element: <InviteParentRoute /> },
      { path: 'account/other-adult', element: <OtherParentRoute /> },
      /*
       * Not guarded by a redirect. An incomplete profile is offered this route
       * from the account panel rather than forced into it: a redirect that fires
       * on every render is how a person ends up unable to reach sign-out, and the
       * API refuses anything that needs a profile regardless.
       */
      { path: 'profile', element: <ProfileRoute /> },
      /*
       * The athlete's own screens (16 October). Shown by AppLayout's athlete
       * frame; an adult who opens one gets the API's refusal, not a screen.
       */
      { path: 'my', element: <MyLessonsRoute /> },
      { path: 'my/:lessonId', element: <MyLessonRoute /> },
      /* The athlete's Performance Signature, from Home (stage 4a). */
      { path: 'signature', element: (
          <Suspense fallback={opening}>
            <AthleteSignatureRoute />
          </Suspense>
        ) },
      { path: 'signature/:section', element: (
          <Suspense fallback={opening}>
            <AthleteSignatureRoute />
          </Suspense>
        ) },
      /*
       * Where the setup email's link lands (ruled 16 October): the parent who
       * asked, signed in, turns a child's sign-in on.
       */
      { path: 'athlete-sign-in', element: <AthleteSignInSetupRoute /> },
      ...toolsRoutes,
      ...practiceRoutes,
      /*
       * Where a family invitation's Sign up lands (stage C): signed in, and let
       * through the profile gate, because it is what gives the parent a profile.
       */
      { path: 'welcome', element: <WelcomeRoute /> },
      /*
       * A second parent's link (canvas row D): the same landing, joining the
       * family instead.
       */
      { path: 'join', element: <JoinRoute /> },
      ...devOnlyRoutes,
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
]);
